import { useMemo, useState } from "react";
import { Icon } from "../icons";
import { printReport } from "../report";
import type { Commitment, Conversation, ConversationReport, Visit } from "../types";

export function CommitmentsView({
  conversations,
  onToggle,
  onOpenConversation,
}: {
  conversations: Conversation[];
  onToggle: (conversationId: string, commitment: Commitment) => void;
  onOpenConversation: (conversationId: string) => void;
}) {
  const [scope, setScope] = useState<"pending" | "done" | "all">("pending");
  const rows = useMemo(
    () => conversations.flatMap((conversation) => conversation.commitments.map((commitment) => ({ conversation, commitment }))),
    [conversations],
  );
  const filtered = rows.filter(({ commitment }) => scope === "all" || commitment.status === scope);
  const overdueLike = filtered.filter(({ commitment }) => commitment.dueAt && /hoy|venc/i.test(commitment.dueAt)).length;

  return (
    <div className="standard-page">
      <PageIntro kicker="SEGUIMIENTO" title="Compromisos claros, con responsable y evidencia." description="Cada acción conserva la conversación que la originó. Completa, reabre o vuelve al mensaje fuente sin perder contexto." action={<div className="segmented"><button className={scope === "pending" ? "active" : ""} onClick={() => setScope("pending")} type="button">Pendientes</button><button className={scope === "done" ? "active" : ""} onClick={() => setScope("done")} type="button">Completados</button><button className={scope === "all" ? "active" : ""} onClick={() => setScope("all")} type="button">Todos</button></div>} />
      <section className="workflow-summary">
        <SummaryMetric icon="check" value={rows.filter(({ commitment }) => commitment.status === "pending").length} label="Pendientes" tone="teal" />
        <SummaryMetric icon="clock" value={overdueLike} label="Atención hoy" tone="amber" />
        <SummaryMetric icon="team" value={new Set(rows.map(({ commitment }) => commitment.responsible)).size} label="Responsables" tone="blue" />
        <SummaryMetric icon="chat" value={new Set(rows.map(({ conversation }) => conversation.id)).size} label="Conversaciones" tone="navy" />
      </section>
      <section className="workflow-list card-panel">
        <div className="table-head"><span>Acción</span><span>Cliente</span><span>Responsable</span><span>Plazo</span><span>Estado</span></div>
        {filtered.map(({ conversation, commitment }) => (
          <article className={`workflow-row ${commitment.status}`} key={`${conversation.id}-${commitment.id}`}>
            <div className="workflow-action-cell"><button className="commitment-check" onClick={() => onToggle(conversation.id, commitment)} type="button">{commitment.status === "done" ? "✓" : ""}</button><button className="workflow-title-link" onClick={() => onOpenConversation(conversation.id)} type="button"><strong>{commitment.description}</strong><small>Ver conversación y evidencia</small></button></div>
            <div><strong>{conversation.customer}</strong><small>{conversation.site}</small></div>
            <div><span className="mini-avatar">{initials(commitment.responsible)}</span><span>{commitment.responsible}</span></div>
            <div><Icon name="clock" size={15} /><span>{commitment.dueAt ?? "Por definir"}</span></div>
            <div><span className={`state-chip ${commitment.status}`}>{commitment.status === "done" ? "Completado" : "Pendiente"}</span></div>
          </article>
        ))}
        {!filtered.length ? <Empty title="No hay compromisos en esta vista" text="Cambia el filtro o revisa una conversación para crear el siguiente paso." icon="check" /> : null}
      </section>
    </div>
  );
}

export function VisitsView({
  visits,
  conversations,
  onOpenConversation,
}: {
  visits: Visit[];
  conversations: Conversation[];
  onOpenConversation: (conversationId: string) => void;
}) {
  const grouped = {
    scheduled: visits.filter((item) => item.status === "scheduled"),
    draft: visits.filter((item) => item.status === "draft"),
    completed: visits.filter((item) => item.status === "completed"),
  };
  return (
    <div className="standard-page">
      <PageIntro kicker="VISITAS" title="Coordina terreno sin reconstruir el contexto." description="Cada visita nace desde una conversación y conserva objetivo, responsable, diagnóstico y próximos pasos." />
      <section className="workflow-summary">
        <SummaryMetric icon="calendar" value={grouped.scheduled.length} label="Agendadas" tone="teal" />
        <SummaryMetric icon="clock" value={grouped.draft.length} label="Por coordinar" tone="amber" />
        <SummaryMetric icon="check" value={grouped.completed.length} label="Completadas" tone="blue" />
        <SummaryMetric icon="team" value={new Set(visits.map((item) => item.assignedTo)).size} label="Técnicos / responsables" tone="navy" />
      </section>
      <div className="visit-grid">
        {visits.map((visit) => {
          const conversation = conversations.find((item) => item.id === visit.conversationId);
          return <article className="visit-card" key={visit.id}><header><span className={`visit-status ${visit.status}`}>{visit.status === "draft" ? "Por coordinar" : visit.status === "scheduled" ? "Agendada" : visit.status === "completed" ? "Completada" : "Cancelada"}</span><button className="icon-only" type="button"><Icon name="more" size={18} /></button></header><div className="visit-date"><span><Icon name="calendar" size={18} /></span><div><strong>{visit.scheduledAt ? formatDate(visit.scheduledAt) : "Fecha por definir"}</strong><small>{visit.scheduledAt ? formatTime(visit.scheduledAt) : "Coordinar con cliente"}</small></div></div><h3>{visit.title}</h3><p>{visit.objective}</p><dl><div><dt>Cliente</dt><dd>{visit.customer}</dd></div><div><dt>Instalación</dt><dd>{visit.site}</dd></div><div><dt>Responsable</dt><dd>{visit.assignedTo}</dd></div></dl><footer><button className="text-link-button" onClick={() => onOpenConversation(visit.conversationId)} disabled={!conversation} type="button">Ver conversación <Icon name="arrow" size={15} /></button></footer></article>;
        })}
        {!visits.length ? <Empty title="Todavía no hay visitas" text="Crea una desde una conversación para arrastrar automáticamente el objetivo y los pendientes." icon="visit" /> : null}
      </div>
    </div>
  );
}

export function ReportsView({
  reports,
  conversations,
  onOpenConversation,
}: {
  reports: ConversationReport[];
  conversations: Conversation[];
  onOpenConversation: (conversationId: string) => void;
}) {
  return (
    <div className="standard-page">
      <PageIntro kicker="REPORTES" title="Borradores verificables, listos para revisar." description="Los reportes reúnen resumen, hallazgos, compromisos y mensajes fuente. La aprobación final siempre es humana." />
      <section className="workflow-summary">
        <SummaryMetric icon="document" value={reports.length} label="Reportes" tone="teal" />
        <SummaryMetric icon="clock" value={reports.filter((item) => item.status === "draft").length} label="Borradores" tone="amber" />
        <SummaryMetric icon="check" value={reports.filter((item) => item.status === "approved").length} label="Aprobados" tone="blue" />
        <SummaryMetric icon="chat" value={new Set(reports.map((item) => item.conversationId)).size} label="Conversaciones" tone="navy" />
      </section>
      <div className="report-grid">
        {reports.map((report) => {
          const conversation = conversations.find((item) => item.id === report.conversationId);
          return <article className="report-card" key={report.id}><div className="report-icon"><Icon name="document" size={22} /></div><div className="report-card-body"><header><span className={`state-chip ${report.status}`}>{report.status === "approved" ? "Aprobado" : "Borrador"}</span><time>{formatDate(report.createdAt)}</time></header><h3>{report.title}</h3><p>{report.summary}</p><div className="report-meta"><span>{report.customer}</span><span>{report.site}</span><span>{report.commitments.length} compromisos</span></div><footer><button className="button soft compact" onClick={() => onOpenConversation(report.conversationId)} type="button">Abrir origen</button><button className="button primary compact" onClick={() => printReport(report, conversation)} type="button"><Icon name="download" size={15} /> Imprimir / PDF</button></footer></div></article>;
        })}
        {!reports.length ? <Empty title="Aún no hay reportes" text="Genera uno desde el panel de contexto de una conversación." icon="document" /> : null}
      </div>
    </div>
  );
}

function PageIntro({ kicker, title, description, action }: { kicker: string; title: string; description: string; action?: React.ReactNode }) {
  return <section className="standard-page-header"><div><p className="section-kicker">{kicker}</p><h1>{title}</h1><p>{description}</p></div>{action ? <div>{action}</div> : null}</section>;
}

function SummaryMetric({ icon, value, label, tone }: { icon: "check" | "clock" | "team" | "chat" | "calendar" | "document"; value: number; label: string; tone: string }) {
  return <article className={`workflow-metric ${tone}`}><span><Icon name={icon} size={19} /></span><div><strong>{value}</strong><small>{label}</small></div></article>;
}

function Empty({ title, text, icon }: { title: string; text: string; icon: "check" | "visit" | "document" }) {
  return <div className="friendly-empty card-panel span-all"><span><Icon name={icon} size={25} /></span><strong>{title}</strong><p>{text}</p></div>;
}

function initials(value: string) {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "CS";
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("es-CL", { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

function formatTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat("es-CL", { hour: "2-digit", minute: "2-digit" }).format(date);
}
